import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '../Modal';
import { api } from '../../lib/api';
import type { NotaFiscalRow } from '../../modules/notas-fiscais/NotasFiscaisLista';

interface Props {
  nota: NotaFiscalRow;
  tipo: 'nfce' | 'nfe';
  onClose: () => void;
  onSuccess: () => void;
}

export function ModalCancelarNota({ nota, tipo, onClose, onSuccess }: Props) {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  // Cancelou na SEFAZ mas o estoque nao voltou — estado PROPRIO, nao um caso de `erro`.
  const [estornoPendente, setEstornoPendente] = useState<string | null>(null);
  const [reparando, setReparando] = useState(false);

  async function repararEstoque() {
    setReparando(true);
    try {
      await api.post(`/api/nfe/${nota.id}/estornar-devolucao-compra`, {});
      setEstornoPendente(null);
      onSuccess();
    } catch (e) {
      setErro(`Nao consegui devolver o estoque: ${(e as Error).message}`);
    } finally {
      setReparando(false);
    }
  }

  const minChars = 3;
  const valido = motivo.trim().length >= minChars;
  const label = tipo === 'nfce' ? 'NFC-e' : 'NF-e';
  const numeroFmt = String(nota.numero).padStart(6, '0');
  const prefixo = `${label} ${numeroFmt} - `;
  const ehDevolucaoCompra = !!nota.nota_entrada_origem_id;

  async function confirmar() {
    if (!valido) return;
    setErro('');
    setLoading(true);
    try {
      const data = await api.post<{
        cancelada?: boolean; xMotivo?: string; cStat?: string;
        estornoEstoque?: 'ok' | 'falhou' | 'nao_se_aplica'; estornoErro?: string | null;
      }>(
        `/api/${tipo}/${nota.id}/cancelar`,
        { motivo: motivo.trim() },
      );
      if (data.cancelada && data.estornoEstoque === 'falhou') {
        // ⛔ As duas metades tem durabilidades diferentes: o evento na SEFAZ e irreversivel, o
        // estorno e local. Chamar isto de "erro" faria o operador tentar de novo um cancelamento
        // que JA passou. A tela tem que dizer as duas coisas na mesma frase.
        setEstornoPendente(data.estornoErro || 'motivo nao informado');
      } else if (data.cancelada) {
        onSuccess();
      } else {
        setErro(`SEFAZ rejeitou o cancelamento: ${data.xMotivo || 'erro desconhecido'}${data.cStat ? ` (cStat ${data.cStat})` : ''}`);
      }
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      aberto
      onFechar={onClose}
      titulo={`Cancelar ${label}`}
      largura={520}
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} disabled={loading || reparando} className="btn">
            {estornoPendente ? 'Fechar' : 'Voltar'}
          </button>
          {/* Some quando o cancelamento ja passou: deixar o botao ali convida a repetir o que nao
              se repete — a SEFAZ recusaria e o operador leria como novo erro. */}
          {!estornoPendente && (
            <button
              type="button"
              onClick={() => void confirmar()}
              disabled={!valido || loading}
              className="btn btn-danger"
            >
              {loading ? 'Enviando...' : `Cancelar ${label}`}
            </button>
          )}
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13 }}>
          <div><strong>Numero:</strong> {numeroFmt} / Serie {nota.serie}</div>
          <div style={{ marginTop: 2 }}>
            <strong>Chave:</strong>{' '}
            <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{nota.chave}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, padding: 10, fontSize: 12, color: '#854d0e' }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            {tipo === 'nfce'
              ? 'NFC-e so pode ser cancelada em ate 30 minutos apos a emissao.'
              : 'NF-e so pode ser cancelada em ate 24 horas apos a autorizacao.'}
          </div>
        </div>

        {/* [DA-559] Esta e' a UNICA nota da casa em que cancelar MEXE no estoque, e mexe pra cima.
            Dizer isso antes do clique nao e' zelo: se a mercadoria ja saiu fisicamente, o estoque
            fica sobrando depois do cancelamento, e quem esta com a mao no botao e' quem sabe se o
            cabo esta no caminhao ou na prateleira. */}
        {/* Cancelou na SEFAZ, estoque ficou pra tras. Nao e' erro do cancelamento — e' meia
            operacao, e a tela tem que separar as duas metades pra ninguem repetir o que ja passou. */}
        {estornoPendente && (
          <div style={{ background: '#f97316', color: '#000', border: '1px solid #c2410c', borderRadius: 6, padding: 10, fontSize: 12, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              A nota FOI cancelada na SEFAZ — mas o estoque nao voltou.
            </div>
            <div>
              O cancelamento valeu e <strong>nao precisa ser repetido</strong>. O que ficou pendente e a
              volta da mercadoria ao estoque. Motivo: <em>{estornoPendente}</em>
            </div>
            <button
              type="button"
              onClick={() => void repararEstoque()}
              disabled={reparando}
              className="btn"
              style={{ marginTop: 8, background: '#000', color: '#fff' }}
            >
              {reparando ? 'Devolvendo...' : 'Devolver o estoque agora'}
            </button>
          </div>
        )}

        {ehDevolucaoCompra && !estornoPendente && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fde68a', color: '#000', border: '1px solid #b45309', borderRadius: 6, padding: 10, fontSize: 12, lineHeight: 1.5 }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              Esta e' uma <strong>devolucao ao fornecedor</strong>: cancelar <strong>devolve a mercadoria ao estoque</strong>,
              na mesma prateleira de onde ela saiu, e a nota de entrada volta a poder ser devolvida.
              {' '}⚠️ <strong>Se a mercadoria ja saiu de fato</strong>, o estoque vai ficar sobrando — confira antes.
            </div>
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Motivo do cancelamento
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (min. {minChars} — nº da nota ja vai no cabecalho)</span>
          </label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value.slice(0, 240))}
            placeholder="Ex: duplicada, erro no cliente, valor errado"
            rows={3}
            style={{
              width: '100%',
              border: '1px solid var(--input-border)',
              borderRadius: 4,
              padding: '6px 8px',
              fontSize: 13,
              resize: 'vertical',
            }}
            autoFocus
            disabled={loading}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
            {motivo.trim().length} caracteres
          </div>
          {valido && (
            <div style={{ marginTop: 6, fontSize: 11, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 4, padding: 8 }}>
              <span style={{ color: 'var(--text-muted)' }}>Sera enviado a SEFAZ:</span>{' '}
              <span style={{ fontFamily: 'monospace' }}>{prefixo}{motivo.trim()}</span>
            </div>
          )}
        </div>

        {erro && (
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, padding: 8, fontSize: 12, color: '#991b1b' }}>
            {erro}
          </div>
        )}
      </div>
    </Modal>
  );
}
